<template>
  <div
    class="team-effectiveness-report team-theme"
    v-if="isLoaded"
    data-test="team-effectiveness-report"
  >
    <template v-if="error">
      <div class="container text-center" style="margin-top: 20px">
        {{ error }}
      </div>
    </template>
    <template v-else-if="showReport">
      <div>
        <div class="container maxWidth-sm">
          <div style="height: 40px"></div>
          <div class="title-container">
            <div class="title-text">
              <div v-html="titleHtml" class="report-title"></div>
              <hr class="hr-magazine hr-magazine-thick hr-magazine-left hr-magazine-short" />
              <p class="report-subtitle">{{ reportDate }}</p>
              <hr class="hr-magazine hr-magazine-thick hr-magazine-left hr-magazine-short" />
            </div>
          </div>
          <ShareButton
            v-if="canShare"
            :reportType="sharingReportType"
            :reportEntityId="sharingEntityId"
            :urlDescription="sharingUrlDescription"
          />
        </div>
        <CmsTextPage v-if="reportDoc" :doc="reportDoc" :context="{ review: existingReview }" />
      </div>
    </template>
    <template v-else-if="needsToCompleteQuestionnaireFirst">
      <div class="row">
        <div class="col-sm-2 col-sm-offset-5 text-center" style="padding-top: 20px">
          <img
            class="img-responsive"
            src="../../assets/team-effectiveness/team-effectiveness-model-logo.png"
          />
        </div>
      </div>
      <div class="row">
        <div class="col-sm-12 text-center">
          <h3 style="margin-left: 5px; margin-right: 5px">
            You haven’t completed a Team Effectiveness Questionnaire on '{{ teamName }}' as part of
            this review yet.
          </h3>
          <p style="margin: 20px 0">Click the button below to complete the Questionnaire.</p>
        </div>
      </div>
      <div class="row">
        <div class="col-sm-12 text-center">
          <a class="btn btn-primary btn-lg" :href="questionnaireUrl">Complete Questionnaire</a>
        </div>
      </div>
      <div class="row">
        <div class="col-sm-6 col-sm-offset-3 text-center" style="margin-top: 20px">
          <p>
            If you’d like to complete a Team Effectiveness Questionnaire on a different team, close
            this session and select another team or create a new one from the top of the page.
          </p>
        </div>
      </div>
    </template>
    <template v-else-if="needsToStartReviewFirst">
      <div class="row">
        <div class="col-sm-2 col-sm-offset-5 text-center" style="padding-top: 20px">
          <img
            class="img-responsive"
            src="../../assets/team-effectiveness/team-effectiveness-model-logo.png"
          />
        </div>
      </div>
      <div class="row">
        <div class="col-sm-12 text-center">
          <h3 style="margin-left: 5px; margin-right: 5px">
            Your team, <b>{{ teamName }}</b
            >, doesn't have a Team Effectiveness Review in progress.
          </h3>
          <p style="margin: 20px 0">
            Click the button below to access the Team Effectiveness Review page and start a new
            review.
          </p>
        </div>
      </div>
      <div class="row">
        <div class="col-sm-12 text-center">
          <a class="btn btn-primary btn-lg" :href="terHighlightsUrl">Go to Team Effectiveness</a>
        </div>
      </div>
      <div class="row">
        <div class="col-sm-6 col-sm-offset-3 text-center" style="margin-top: 20px">
          <p>
            If you’d like to complete a Team Effectiveness Questionnaire on a different team, close
            this session and select another team or create a new one from the top of the page.
          </p>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import axios from "axios";
import moment from "dayjs";
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { CmsDocumentTypes, CmsResourceData } from "../../services/cms/cms-type-definitions";
import CmsTextPage from "../cms/cms-text-page.vue";
import cmsContentMap from "../../services/cms/cms-content-map";
import {
  TeamApiModel,
  ReportSharingTypeApiModel,
  TeamEffectivenessReviewApiModel,
} from "../../api-models";
import { CmsReport } from "../../services/cms/types/cms-report";
import ShareButton from "../../components/share-button.vue";
import cms from "../../services/cms/cms";
import {
  getTeamEffectivenessReportFlexible,
  getTeamEffectivenessReportTeamId,
  getTeamEffectivenessReportReviewId,
} from "../../components/team-effectiveness/get-team-effectiveness-report-team-id";

const needsToCompleteQuestionnaireFirst = ref(false);
const needsToStartReviewFirst = ref(false);
const showReport = ref(false);
const isLoaded = ref(false);
const reportDoc = ref<CmsReport | null>(null);
const error = ref("");
const newReviewTeamDetails = ref<TeamApiModel | null>(null);
const existingReview = ref<TeamEffectivenessReviewApiModel | null>(null);

const props = defineProps<{ doc: CmsReport }>();

const route = useRoute();
const router = useRouter();

const teamId = computed(() => getTeamEffectivenessReportTeamId(route));
const reviewId = computed(() => getTeamEffectivenessReportReviewId(route));
const teamName = computed(() => {
  if (existingReview.value) {
    return existingReview.value.subjectTeamDetails.name;
  }

  return newReviewTeamDetails.value ? newReviewTeamDetails.value.name : "";
});

const sharingUrlDescription = computed(() =>
  !teamName.value ? "" : `Team Effectiveness Report For ${teamName.value}`,
);
const sharingReportType = computed(() =>
  reviewId.value ? "SpecificTeamEffectivenessReport" : "LatestTeamEffectivenessReport",
);
const sharingEntityId = computed(() => {
  if (!existingReview.value) {
    return "";
  }

  return sharingReportType.value === "SpecificTeamEffectivenessReport"
    ? existingReview.value.id
    : existingReview.value.subjectTeamId;
});

const canShare = computed(() => !!existingReview.value);
const questionnaireUrl = computed(() => `/team/effectivenessquestionnaire/${reviewId.value}`);
const terHighlightsUrl = computed(() => `/team/effectiveness/${teamId.value}`);
const reportDate = computed(() => {
  if (!existingReview.value || existingReview.value.questionnaires.length === 0) {
    return "";
  }

  return existingReview.value.completedDate
    ? moment(existingReview.value.completedDate).format("D MMMM YYYY")
    : moment([...existingReview.value.questionnaires].reverse()[0].completedDate).format(
        "D MMMM YYYY",
      );
});

const titleHtml = computed(() => {
  const cmsVariableOverrides = {};
  cmsVariableOverrides[cmsContentMap.variableNames.teamName] = teamName.value;
  return cms.asHtml((props.doc?.data as CmsResourceData)?.title, cmsVariableOverrides);
});

function getTeamDetails(): Promise<TeamApiModel> {
  return axios.get(`/api/teams/${teamId.value}`).then((resp) => {
    const data = resp.data as TeamApiModel;
    return data;
  });
}

onMounted(async () => {
  if (!teamId.value && !reviewId.value) {
    error.value = "This report could not be found.";
    return;
  }

  const reportModel = await getTeamEffectivenessReportFlexible(reviewId.value, teamId.value);
  if (reportModel == null) {
    error.value =
      "There was a problem getting your report. Please try refreshing the page, and if that doesn't work, you can contact us at support@gyreteams.com";
  } else {
    if (reportModel.review.questionnaires.length < 2) {
      await router.push("/NotFound");
    }

    existingReview.value = reportModel.review;
    needsToCompleteQuestionnaireFirst.value = reportModel.status === "ShouldCompleteQuestionnaire";
    needsToStartReviewFirst.value = reportModel.status === "ShouldStartReview";
    showReport.value = !!reportModel.review && reportModel.status === "ReportAvailable";
    error.value =
      reportModel.status === "ReportNotAvailable"
        ? "The report you requested does not exist, or is not currently available to you"
        : "";
    if (!existingReview.value && needsToStartReviewFirst.value) {
      newReviewTeamDetails.value = await getTeamDetails();
    }
    if (showReport.value) {
      if (!props.doc) {
        reportDoc.value = await cms.getByUid(
          CmsDocumentTypes.CmsReport,
          cmsContentMap.teamEffectiveness.reportUid,
        );
      } else {
        reportDoc.value = props.doc;
      }
    }
  }

  isLoaded.value = true;
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="less">
section.cms-section:first-child {
  margin-top: 90px;
  margin-bottom: 100px;
}
</style>
